import React from 'react';

interface MonkeyLoaderProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const MonkeyLoader: React.FC<MonkeyLoaderProps> = ({
  size = 'md',
  className = ''
}) => {
  // Size configurations for the sprite (based on 100px frames from the sprite sheet)
  const sizeConfig = {
    sm: { sprite: 'w-20 h-20' },  // 80px display size
    md: { sprite: 'w-24 h-24' },  // 96px display size (closest to 100px)
    lg: { sprite: 'w-28 h-28' }   // 112px display size
  };

  React.useEffect(() => {
    // Add CSS keyframes for monkey sprite animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes monkey-somersault {
        /* Frame 12: Bottom row, 4th column */
        0% { background-position: -300px -200px; }
        12.5% { background-position: -300px -200px; }

        /* Frame 2: Top row, 2nd column */
        12.51% { background-position: -100px 0px; }
        25% { background-position: -100px 0px; }

        /* Frame 3: Top row, 3rd column */
        25.01% { background-position: -200px 0px; }
        37.5% { background-position: -200px 0px; }

        /* Frame 4: Top row, 4th column */
        37.51% { background-position: -300px 0px; }
        50% { background-position: -300px 0px; }

        /* Frame 8: Middle row, 4th column */
        50.01% { background-position: -300px -100px; }
        62.5% { background-position: -300px -100px; }

        /* Frame 9: Bottom row, 1st column */
        62.51% { background-position: 0px -200px; }
        75% { background-position: 0px -200px; }

        /* Frame 10: Bottom row, 2nd column */
        75.01% { background-position: -100px -200px; }
        87.5% { background-position: -100px -200px; }

        /* Frame 11: Bottom row, 3rd column */
        87.51% { background-position: -200px -200px; }
        100% { background-position: -200px -200px; }
      }


      /* For users who prefer reduced motion, slow down the animation */
      @media (prefers-reduced-motion: reduce) {
        .monkey-sprite {
          animation-duration: 2s !important; /* Standard duration for accessibility */
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  const config = sizeConfig[size];

  return (
    <div className={`flex flex-col items-center justify-center py-8 ${className}`}>
      {/* Blue monkey sprite animation */}
      <div className="flex items-center justify-center">
        <div
          className={`${config.sprite} monkey-sprite`}
          style={{
            backgroundImage: 'url(/images/monkey-sprite.png)',
            backgroundSize: '400px 300px', // 4x3 grid (100px per frame)
            backgroundRepeat: 'no-repeat',
            backgroundPosition: '0px 0px',
            animation: 'monkey-somersault 2s linear infinite',
            imageRendering: 'pixelated' // Ensures crisp sprite rendering
          }}
        />
      </div>

      {/* Loading text */}
      <div className="mt-6 text-center">
        <p className="text-sm text-gray-600 font-medium animate-pulse">
          Translating...
        </p>
        <div className="flex justify-center mt-3 space-x-1">
          <div
            className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
            style={{ animationDelay: '0ms' }}
          />
          <div
            className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
            style={{ animationDelay: '150ms' }}
          />
          <div
            className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
            style={{ animationDelay: '300ms' }}
          />
        </div>
      </div>
    </div>
  );
};

export default MonkeyLoader;